import { useT } from '@/core/I18nProvider';

import { BEAT } from './branch-beats';
import { CardTitle, Spacer } from './branch-parts';

import type { Branch } from './story-data';
import type { ReactElement } from 'react';

/**
 * Branch 3 · Policy — a governance draft assembled from two answers.
 *
 * Hoisted out of `BranchCard.tsx` at that file's OWN section-3 boundary, which
 * had crossed the ~300-line ceiling in `.claude/rules/architecture.md`. A leaf:
 * it takes the branch and imports nothing back from its parent, which is what
 * makes this the right cut rather than the biggest one.
 *
 * It sits beside `branch-bodies-2.tsx` (branches 4–6) for the reason that file
 * exists — six deliberately unlike interfaces do not belong in one module just
 * because one switch dispatches to them.
 */

export function PolicyBody({ branch }: { readonly branch: Branch }): ReactElement {
  const { t } = useT();
  const { at } = branch;
  return (
    <>
      <div className="mt-3 flex flex-none flex-col gap-[7px]">
        {['q1', 'q2'].map((q, i) => (
          <div
            key={q}
            className="flex items-center gap-2 rounded-[9px] border border-[#eff1f5] bg-[#f9fafc] px-2.5 py-[7px]"
            style={{
              animation: `sxUpS .4s ease ${(at + BEAT.body + i * 0.45).toFixed(2)}s backwards`,
            }}
          >
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-[#5f6673]">
              {t(`home.story.policy.${q}`)}
            </span>
            <span
              className="flex-none whitespace-nowrap rounded-full bg-[rgba(37,99,235,.1)] px-2.5 py-0.5 text-xs font-semibold text-[#2348db]"
              style={{
                animation: `sxPopS .4s cubic-bezier(.34,1.56,.64,1) ${(at + BEAT.body + 0.4 + i * 0.45).toFixed(2)}s backwards`,
              }}
            >
              {t(`home.story.policy.${q}Answer`)}
            </span>
          </div>
        ))}
      </div>
      <CardTitle at={at} offset={3.5}>
        {t('home.story.policy.title')}
      </CardTitle>
      <div className="mt-2.5 flex flex-none flex-col gap-2 border-s-2 border-[rgba(122,24,232,.2)] ps-3">
        {['c1', 'c2'].map((clause, i) => (
          <div
            key={clause}
            className="flex gap-2.5 text-[12.5px] leading-snug text-[#111827]"
            style={{
              animation: `sxInL .4s cubic-bezier(.16,1,.3,1) ${(at + 3.75 + i * 0.15).toFixed(2)}s backwards`,
            }}
          >
            <span className="mt-0.5 flex-none font-mono text-[9.5px] text-[#6d4bb8]">0{i + 1}</span>
            <span>{t(`home.story.policy.${clause}`)}</span>
          </div>
        ))}
      </div>
      <Spacer />
      <div
        className="flex flex-none items-center gap-2 rounded-[10px] border border-[rgba(122,24,232,.14)] bg-[rgba(122,24,232,.05)] px-2.5 py-2 text-[11.5px] text-[#54406b]"
        style={{ animation: `sxUpS .45s ease ${(at + 4.2).toFixed(2)}s backwards` }}
      >
        <span
          aria-hidden="true"
          className="size-1.5 flex-none rounded-full bg-[color:var(--bd-purple)]"
        />
        {t('home.story.policy.governs')}
      </div>
    </>
  );
}
